import React, { useEffect, useRef, useState, useCallback } from 'react';
import RevolutCheckout from '@revolut/checkout';
import axios from 'axios';
import { Loader2, CreditCard, AlertCircle, Smartphone } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared Revolut payment form: Revolut Pay button, Apple/Google Pay button,
 * and a manual card field. Use everywhere we accept payment.
 *
 * Props:
 *  - onSuccess(intentId)  → called with `revolut-<orderId>` on success
 *  - onError(message)     → called when payment fails
 *  - formatPrice(amount)  → currency-aware price formatter
 *  - total                → amount to charge in user-selected currency
 *  - totalInAud           → AUD-equivalent (used for Revolut Pay minor units)
 *  - revolutToken         → publicId for createCardField (from create-intent)
 *  - revolutOrderId       → internal order id, returned on success
 *  - user                 → { email, firstName?, lastName?, name? }
 *  - country / plan       → { id, name } / { id, packageId, name }
 *  - currentCurrency      → { code }
 *  - description          → optional override for the payment description
 */
export const RevolutPaymentForm = ({
  onSuccess,
  onError,
  formatPrice,
  total,
  revolutToken,
  revolutOrderId,
  user,
  totalInAud,
  country,
  plan,
  currentCurrency,
  description,
  /**
   * When true (e.g. Top-Up / Auto Top-Up setup), the card payment is submitted
   * with `savePaymentMethodFor: 'merchant'` so Revolut keeps the card on file
   * for future merchant-initiated transactions. For one-time Checkout flows
   * this MUST be false — passing it on a non-MIT order causes Revolut to
   * silently reject the submit (incident 2026-06-18: "Processing..." hang).
   */
  savePaymentMethod = false,
}) => {
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';

  const cardFieldRef = useRef(null);
  const paymentRequestRef = useRef(null);
  const revolutPayRef = useRef(null);
  const cardInstanceRef = useRef(null);
  const paymentRequestInstanceRef = useRef(null);
  const revolutPayInstanceRef = useRef(null);
  const submitFinishRef = useRef(null);
  const stallTimerRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [cardMounted, setCardMounted] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardholderName, setCardholderName] = useState(
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.name || ''
  );

  const defaultDescription =
    description ||
    `${country?.name || 'eSIM'} - ${plan?.name || 'Plan'}`;

  // Initialize card field
  useEffect(() => {
    if (!revolutToken || !cardFieldRef.current) return;

    if (cardInstanceRef.current && cardInstanceRef.current.destroy) {
      cardInstanceRef.current.destroy();
      cardInstanceRef.current = null;
      setCardMounted(false);
      setCardComplete(false);
    }

    let mounted = true;

    const initRevolutWidget = async () => {
      try {
        const instance = await RevolutCheckout(revolutToken);
        if (!mounted) return;

        const cardField = instance.createCardField({
          target: cardFieldRef.current,
          onSuccess() {
            if (submitFinishRef.current) submitFinishRef.current();
            if (onSuccess) onSuccess(`revolut-${revolutOrderId}`);
          },
          onError(error) {
            if (submitFinishRef.current) submitFinishRef.current();
            setMessage(error?.message || 'Payment failed. Please try again.');
            setIsSubmitting(false);
            if (onError) onError(error?.message || 'Payment failed');
          },
          onCancel() {
            if (submitFinishRef.current) submitFinishRef.current();
            setMessage('Payment was cancelled.');
            setIsSubmitting(false);
          },
          // Track card-field readiness so we can (a) show instant feedback
          // instead of the 20 s safety-net timeout and (b) disable the Pay
          // button until every card sub-field is valid + complete.
          onStatusChange(status) {
            if (status && typeof status.completed === 'boolean') {
              setCardComplete(status.completed);
            }
          },
          styles: {
            default: {
              color: isDark ? '#ffffff' : '#1f2937',
              fontSize: '16px',
              '::placeholder': { color: isDark ? '#a3a3a3' : '#9ca3af' },
            },
          },
        });

        cardInstanceRef.current = cardField;
        setCardMounted(true);
      } catch (err) {
        console.error('Failed to init Revolut widget:', err);
        setMessage('Payment widget failed to load. Please try again.');
      }
    };

    initRevolutWidget();

    return () => {
      mounted = false;
      if (cardInstanceRef.current && cardInstanceRef.current.destroy) {
        cardInstanceRef.current.destroy();
      }
      cardInstanceRef.current = null;
    };
    // eslint-disable-next-line
  }, [revolutToken, isDark]);

  // Initialize Apple Pay / Google Pay
  useEffect(() => {
    if (!paymentRequestRef.current) return;

    if (paymentRequestInstanceRef.current && paymentRequestInstanceRef.current.destroy) {
      paymentRequestInstanceRef.current.destroy();
      paymentRequestInstanceRef.current = null;
    }
    if (paymentRequestRef.current) paymentRequestRef.current.innerHTML = '';

    let mounted = true;
    const publicKey = process.env.REACT_APP_REVOLUT_PUBLIC_KEY;
    if (!publicKey) return;

    const walletCurrency = currentCurrency?.code || 'AUD';
    const walletAmount = parseFloat((total || 0).toFixed(2));
    if (walletAmount <= 0) return;

    const initWalletPay = async () => {
      try {
        const { paymentRequest } = await RevolutCheckout.payments({
          locale: 'en',
          publicToken: publicKey,
          mode: 'prod',
        });
        if (!mounted) return;
        if (paymentRequestRef.current) paymentRequestRef.current.innerHTML = '';

        let walletOrderId = null;

        const instance = paymentRequest(paymentRequestRef.current, {
          currency: walletCurrency,
          amount: Math.round(walletAmount * 100),
          createOrder: async () => {
            const resp = await axios.post(`${API}/revolut/create-order`, {
              amount: walletAmount,
              currency: walletCurrency,
              description: defaultDescription,
              user_email: user?.email || '',
              plan_id: plan?.packageId || plan?.id || '',
              country_id: country?.id || '',
              // Apple Pay / Google Pay network tokens cannot be saved for
              // merchant-initiated (MIT) reuse — requesting it makes Revolut
              // reject the wallet payment after the sheet authorises.
              save_payment_method: false,
            });
            if (resp.data.success) {
              walletOrderId = resp.data.order_id;
              return { publicId: resp.data.token };
            }
            throw new Error('Failed to create order');
          },
          onSuccess() {
            if (onSuccess) onSuccess(`revolut-${walletOrderId}`);
          },
          onError(error) {
            // eslint-disable-next-line no-console
            console.error('[Revolut Wallet Pay] onError', {
              type: error?.type,
              code: error?.code,
              message: error?.message,
              currency: walletCurrency,
              amountMinor: Math.round(walletAmount * 100),
            });
            setMessage(error?.message || 'Payment failed.');
            if (onError) onError(error?.message || 'Payment failed');
          },
          buttonStyle: { radius: 'large', size: 'large', action: 'pay', variant: isDark ? 'light' : 'dark' },
        });

        paymentRequestInstanceRef.current = instance;

        // canMakePayment() returns either null/false (nothing available), or
        // an object like { applePay: true } on iOS Safari with a card in
        // Wallet AND the domain verified in Revolut's merchant dashboard.
        // If the domain isn't registered on Revolut's dashboard, this returns
        // false EVEN if the domain-association file is hosted correctly.
        const method = await instance.canMakePayment();
        // eslint-disable-next-line no-console
        console.info('[Revolut Wallet Pay] canMakePayment() ->', method, {
          ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          host: typeof window !== 'undefined' ? window.location.host : '',
          currency: walletCurrency,
        });
        if (method && mounted) {
          instance.render();
        } else {
          instance.destroy();
          paymentRequestInstanceRef.current = null;
          if (paymentRequestRef.current) paymentRequestRef.current.innerHTML = '';
        }
      } catch (err) {
        console.error('Wallet pay init error:', err);
      }
    };

    initWalletPay();

    return () => {
      mounted = false;
      if (paymentRequestInstanceRef.current && paymentRequestInstanceRef.current.destroy) {
        paymentRequestInstanceRef.current.destroy();
      }
      paymentRequestInstanceRef.current = null;
      if (paymentRequestRef.current) paymentRequestRef.current.innerHTML = '';
    };
    // eslint-disable-next-line
  }, [total, currentCurrency?.code, cardMounted]);

  // Initialize Revolut Pay button
  useEffect(() => {
    if (!revolutPayRef.current) return;

    if (revolutPayInstanceRef.current && revolutPayInstanceRef.current.destroy) {
      revolutPayInstanceRef.current.destroy();
      revolutPayInstanceRef.current = null;
    }
    if (revolutPayRef.current) revolutPayRef.current.innerHTML = '';

    let mounted = true;
    const publicKey = process.env.REACT_APP_REVOLUT_PUBLIC_KEY;
    if (!publicKey) return;

    const amountMinor = Math.round((totalInAud || total) * 100);
    if (amountMinor <= 0) return;

    const initRevolutPay = async () => {
      try {
        const { revolutPay } = await RevolutCheckout.payments({
          locale: 'en',
          publicToken: publicKey,
          mode: 'prod',
          country: 'AU',
        });
        if (!mounted) return;
        if (revolutPayRef.current) revolutPayRef.current.innerHTML = '';

        let revPayOrderId = null;
        const revPayCurrency = currentCurrency?.code || 'AUD';
        const revPayAmount = parseFloat((total || totalInAud).toFixed(2));

        const paymentOptions = {
          currency: revPayCurrency,
          totalAmount: Math.round(revPayAmount * 100),
          createOrder: async () => {
            const resp = await axios.post(`${API}/revolut/create-order`, {
              amount: revPayAmount,
              currency: revPayCurrency,
              description: defaultDescription,
              user_email: user?.email || '',
              plan_id: plan?.packageId || plan?.id || '',
              country_id: country?.id || '',
            });
            if (resp.data.success) {
              revPayOrderId = resp.data.order_id;
              return { publicId: resp.data.token };
            }
            throw new Error('Failed to create order');
          },
          buttonStyle: { radius: 'large', size: 'large', variant: isDark ? 'light' : 'dark' },
        };

        revolutPay.mount(revolutPayRef.current, paymentOptions);
        revolutPayInstanceRef.current = revolutPay;

        revolutPay.on('payment', (event) => {
          if (event.type === 'success') {
            if (onSuccess) onSuccess(`revolut-${revPayOrderId}`);
          } else if (event.type === 'error') {
            setMessage(event.error?.message || 'Payment failed.');
            if (onError) onError(event.error?.message || 'Payment failed');
          } else if (event.type === 'cancel') {
            setMessage('Payment was cancelled.');
          }
        });
      } catch (err) {
        console.error('Revolut Pay init error:', err);
      }
    };

    initRevolutPay();

    return () => {
      mounted = false;
      if (revolutPayInstanceRef.current && revolutPayInstanceRef.current.destroy) {
        revolutPayInstanceRef.current.destroy();
      }
      revolutPayInstanceRef.current = null;
      if (revolutPayRef.current) revolutPayRef.current.innerHTML = '';
    };
    // eslint-disable-next-line
  }, [totalInAud, total, cardMounted, isDark]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!cardInstanceRef.current || isSubmitting) return;

    // Pre-submit validation — instant feedback so users don't wait the
    // 20 s safety-net timeout for an empty/incomplete card.
    const nameOk = cardholderName.trim().length > 0;
    if (!nameOk) {
      setMessage('Please enter the cardholder name.');
      return;
    }
    if (!cardComplete) {
      setMessage('Please enter your full card details (number, expiry and CVC).');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    // Safety timeout — if none of the widget callbacks (onSuccess/onError/
    // onCancel) fire within 20 s, unstick the UI so the customer can try
    // again. This covers popup-blocker cases, silent Revolut widget stalls
    // where the 3D-Secure challenge never renders, and empty/invalid card
    // submits (Revolut's card field does not fire onError for those).
    // Customer report 2026-07-27: "sits processing, no popups".
    let settled = false;
    const friendlyStallMsg =
      "We couldn't process your card. Please check your card details (and allow popups for 3D Secure), then try again.";

    const finish = () => {
      if (settled) return;
      settled = true;
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
      submitFinishRef.current = null;
    };

    stallTimerRef.current = setTimeout(() => {
      if (settled) return;
      settled = true;
      stallTimerRef.current = null;
      submitFinishRef.current = null;
      setIsSubmitting(false);
      setMessage(friendlyStallMsg);
      if (onError) onError(friendlyStallMsg);
    }, 20000);

    // Assign BEFORE submit() so if Revolut's callbacks fire synchronously
    // or very quickly, they can still clear the stall timer.
    submitFinishRef.current = finish;

    try {
      const submitPromise = cardInstanceRef.current.submit({
        name: cardholderName.trim() || 'Card Holder',
        email: user?.email || '',
        ...(savePaymentMethod ? { savePaymentMethodFor: 'merchant' } : {}),
        billingAddress: { countryCode: 'AU', postcode: '2000' },
      });

      // Revolut's submit() returns a Promise in newer SDK versions. When
      // present, awaiting it surfaces network errors that would otherwise
      // vanish (no callback fired). When absent, the promise resolves
      // undefined immediately, so the callbacks continue to drive the UI.
      if (submitPromise && typeof submitPromise.then === 'function') {
        await submitPromise;
      }
      // Widget callbacks (onSuccess/onError/onCancel) still drive the
      // final UI state and clear the stall timer via submitFinishRef.
    } catch (err) {
      finish();
      const msg = err?.message || 'Payment failed to submit. Please try again.';
      setMessage(msg);
      setIsSubmitting(false);
      if (onError) onError(msg);
    }
  }, [isSubmitting, user, cardholderName, savePaymentMethod, onError, cardComplete]);

  // Clear any in-flight stall timer on unmount so it can't fire against
  // an already-navigated-away component (would setState-after-unmount).
  useEffect(() => {
    return () => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
      submitFinishRef.current = null;
    };
  }, []);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div
        key={`revolut-pay-${currentCurrency?.code || 'AUD'}-${(totalInAud || total || 0).toFixed(2)}`}
        ref={revolutPayRef}
        data-testid="revolut-pay-button"
        className="revolut-pay-clip rounded-2xl overflow-hidden"
        style={{ height: 56 }}
      />

      <div
        key={`wallet-pay-${currentCurrency?.code || 'AUD'}-${(total || 0).toFixed(2)}`}
        ref={paymentRequestRef}
        data-testid="revolut-wallet-pay"
        className="min-h-[48px] rounded-2xl overflow-hidden"
        style={{
          isolation: 'isolate',
          WebkitMaskImage: 'linear-gradient(#fff, #fff)',
          transform: 'translateZ(0)',
        }}
      />

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700" />
        <span className="text-sm text-gray-500 dark:text-neutral-500 font-medium">Or enter your card details manually:</span>
        <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700" />
      </div>

      <div className="space-y-3">
        <input
          type="text"
          placeholder="Cardholder name (e.g. John Smith)"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:border-gray-400 dark:focus:border-neutral-400 text-base bg-white dark:bg-neutral-900 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-neutral-500"
          data-testid="cardholder-name-input"
        />
        <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4 bg-white dark:bg-neutral-900 overflow-hidden min-h-[60px]">
          <div ref={cardFieldRef} data-testid="revolut-card-field" />
          {!cardMounted && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-neutral-500" />
              <span className="ml-2 text-sm text-gray-400 dark:text-neutral-500">Loading payment form...</span>
            </div>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !cardMounted}
        className="w-full py-4 bg-black text-white rounded-xl font-bold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 dark:border dark:border-white"
        data-testid="revolut-pay-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard size={20} />
            Pay {formatPrice(total)} with Card
          </>
        )}
      </button>

      <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-stone-700 rounded-xl border border-blue-100 dark:border-stone-500">
        <Smartphone className="text-blue-600 dark:text-white flex-shrink-0 mt-0.5" size={20} />
        <p className="text-xs text-gray-600 dark:text-white leading-relaxed">
          <span className="font-semibold text-gray-800 dark:text-white">Need a verification code?</span> Turn your SIM card on (keep roaming off) to receive the code without charges!
        </p>
      </div>

      {message && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-3 bg-red-50 dark:bg-red-500/10 rounded-xl">
          <AlertCircle size={18} />
          <span>{message}</span>
        </div>
      )}
    </form>
  );
};

export default RevolutPaymentForm;
